import { ClientError, Metadata, Status } from 'nice-grpc-web';
import { v4 as uuidv4 } from 'uuid';
// import { NodeHttpTransport } from '@improbable-eng/grpc-web';

import { User } from '../state/reducers/authSlice';
import { MarketItemSubscribed } from '../state/reducers/celerMarketSlice';

import { marketDataServiceClient } from '@/helpers/environmentHelper';
import { appendSubscriptions, PairMap, removeMarket, setActivePair } from '@/state/reducers/marketPairSlice';
import { AppDispatch, RootState, store } from '@/state/store';
import { Logger } from '@/utils/logger';
import { AssetType } from '../compiled_proto/com/celertech/marketdata/api/enums/AssetTypeProto';
import { MarketDataBookType } from '../compiled_proto/com/celertech/marketdata/api/enums/MarketDataBookTypeProto';
import { MarketDataMidrateLevelType } from '../compiled_proto/com/celertech/marketdata/api/enums/MarketDataMidrateLevelTypeProto';
import { MarketDataRequestType } from '../compiled_proto/com/celertech/marketdata/api/enums/MarketDataRequestTypeProto';
import { MarketDataUpdateType } from '../compiled_proto/com/celertech/marketdata/api/enums/MarketDataUpdateTypeProto';
import { ProductType } from '../compiled_proto/com/celertech/marketdata/api/enums/ProductTypeProto';
import { MarketDataSubscriptionRequest } from '../compiled_proto/com/celertech/marketdata/api/price/UpstreamPriceProto';
import { toastLogout } from '../utils/hooks/useToast';
import { logToServer } from './LogService';

const exchangeCode = window.config.integration.celertech.exchangeCode || 'XCEL';

export async function addMultipleMarkets(credentials: User, pairs: string[]) {
    const pairsToSubscribe = pairs.filter((pair) => {
        const state: RootState = store.getState();
        const subscriptions = state.marketPair.subscriptions || {};
        const activePair = state.marketPair.activePair;

        // find if subscription of pair already exists
        const subscribedPairs = Object.values(subscriptions);
        if (subscribedPairs.find((sub) => sub.pair === pair) || activePair?.celer === pair) return false;
        else return true;
    });

    const reqCollection = pairsToSubscribe.map((pair) => generateRequest(credentials, pair));

    await marketDataServiceClient.unary.createPriceSubscriptionRequestBatch(
        { subscriptionRequestCollection: reqCollection.map((req) => req.req) },
        { metadata: Metadata({ 'authorization-token': credentials.authToken }) }
    );

    return reqCollection.map(
        ({ pair, dataReqId, req }) =>
            ({
                exchangeCode: req.exchangeCode,
                securityCode: pair,
                subscriptionId: dataReqId,
                settlementType: req.settlementType
            } as MarketItemSubscribed)
    );
}

export async function addMarketFull(pair: string) {
    const state: RootState = store.getState();
    const credentials = state.auth.user as User;
    const dispatch: AppDispatch = store.dispatch;
    const subscriptions = state.marketPair.subscriptions || {};

    // find if subscription of pair already exists
    const subscribedKeys = Object.keys(subscriptions);
    const foundKey = subscribedKeys.find((key) => subscriptions[key].pair === pair) || '';

    if (subscriptions[foundKey]?.type === 'full') {
        return;
    } else if (subscriptions[foundKey]?.type === 'tob') {
        // remove existing subscription of pair
        cancelMarket(credentials, {
            exchangeCode: subscriptions[foundKey].exchange,
            securityCode: pair,
            settlementType: subscriptions[foundKey].settlementType,
            subscriptionId: foundKey
        });
        dispatch(removeMarket({ subscriptionId: foundKey }));
    }

    const dataSubscriptionId = foundKey || uuidv4();
    const { marketExchangeCode, marketSettlementType, marketProductType } = getMarketDetails(pair);

    try {
        await marketDataServiceClient.unary.createPriceSubscriptionRequest(
            {
                assetType: AssetType.FX,
                clientRequestId: uuidv4(),
                exchangeCode: marketExchangeCode,
                settlementType: marketSettlementType,
                marketDataBookType: MarketDataBookType.FULL_BOOK,
                marketDataMidrateLevelType: MarketDataMidrateLevelType.ALL,
                marketDataRequestId: dataSubscriptionId,
                marketDataRequestType: MarketDataRequestType.SNAPSHOT_PLUS_UPDATES,
                marketDataUpdateType: MarketDataUpdateType.FULL_SNAPSHOT,
                productType: marketProductType,
                requestedByUser: credentials.username,
                securityCode: pair,
                securityId: pair
            },
            {
                metadata: Metadata({
                    'authorization-token': credentials.authToken
                })
            }
        );
        dispatch(
            appendSubscriptions({
                [dataSubscriptionId]: {
                    type: 'full',
                    pair,
                    exchange: marketExchangeCode,
                    settlementType: marketSettlementType
                }
            })
        );
    } catch (error) {
        if (error instanceof ClientError) {
            if ([Status.UNAUTHENTICATED, Status.UNKNOWN].includes(error.code)) {
                Logger({
                    title: `Inbound: Market Instrument Data - code: ${error?.code}.`,
                    callback: () => {
                        console.log({ error });
                        console.log(`Logging user out, please contact technical support.`);
                    }
                });
                logToServer(
                    'warn',
                    `User [${credentials.username}] has been logged out due to Market Instrument Data error - Code: ${error?.code}.`
                );
                toastLogout(credentials);
            }
            return null;
        }
    }

    return {
        exchangeCode: marketExchangeCode,
        securityCode: pair,
        settlementType: marketSettlementType,
        subscriptionId: dataSubscriptionId
    } as MarketItemSubscribed;
}

export async function addMarketTob(pair: string) {
    const state: RootState = store.getState();
    const credentials = state.auth.user as User;
    const dispatch: AppDispatch = store.dispatch;
    const subscriptions = state.marketPair.subscriptions || {};

    // find if subscription of pair already exists
    const subscribedKeys = Object.keys(subscriptions);
    const foundKey = subscribedKeys.find((key) => subscriptions[key].pair === pair) || '';

    if (subscriptions[foundKey]?.type === 'tob') {
        return;
    } else if (subscriptions[foundKey]?.type === 'full') {
        // remove existing subscription of pair
        cancelMarket(credentials, {
            exchangeCode: subscriptions[foundKey].exchange,
            securityCode: pair,
            settlementType: subscriptions[foundKey].settlementType,
            subscriptionId: foundKey
        });
        dispatch(removeMarket({ subscriptionId: foundKey }));
    }

    const dataSubscriptionId = foundKey || uuidv4();
    const { marketExchangeCode, marketSettlementType, marketProductType } = getMarketDetails(pair);

    try {
        await marketDataServiceClient.unary.createPriceSubscriptionRequest(
            {
                assetType: AssetType.FX,
                clientRequestId: uuidv4(),
                exchangeCode: marketExchangeCode,
                settlementType: marketSettlementType,
                marketDataBookType: MarketDataBookType.TOP_OF_THE_BOOK,
                marketDataMidrateLevelType: MarketDataMidrateLevelType.TOB,
                marketDataRequestId: dataSubscriptionId,
                marketDataRequestType: MarketDataRequestType.SNAPSHOT_PLUS_UPDATES,
                marketDataUpdateType: MarketDataUpdateType.FULL_SNAPSHOT,
                productType: marketProductType,
                requestedByUser: credentials.username,
                securityCode: pair,
                securityId: pair
            },
            {
                metadata: Metadata({
                    'authorization-token': credentials.authToken
                })
            }
        );
        dispatch(
            appendSubscriptions({
                [dataSubscriptionId]: {
                    type: 'tob',
                    pair,
                    exchange: marketExchangeCode,
                    settlementType: marketSettlementType
                }
            })
        );
    } catch (error) {
        if (error instanceof ClientError) {
            if ([Status.UNAUTHENTICATED, Status.UNKNOWN].includes(error.code)) {
                Logger({
                    title: `Inbound: Market Instrument Data - code: ${error?.code}.`,
                    callback: () => {
                        console.log({ error });
                        console.log(`Logging user out, please contact technical support.`);
                    }
                });
                logToServer(
                    'warn',
                    `User [${credentials.username}] has been logged out due to Market Instrument Data error - Code: ${error?.code}.`
                );
                toastLogout(credentials);
            }
            return null;
        }
    }

    return {
        exchangeCode: marketExchangeCode,
        securityCode: pair,
        settlementType: marketSettlementType,
        subscriptionId: dataSubscriptionId
    } as MarketItemSubscribed;
}

export async function switchOrderBook(currentPair: PairMap) {
    const state: RootState = store.getState();
    const dispatch: AppDispatch = store.dispatch;
    const prevPair = state.marketPair.activePair;
    const currPair = currentPair.celer;

    const watchLists = state.traderMarket.watchlists;
    const selectedWatchList = state.traderMarket.selectedWatchList;

    // Downgrade previous pair to tob
    const watchList = selectedWatchList || watchLists[0];

    if (prevPair.celer) {
        const exist = watchList.markets.find((m) => m.celer === prevPair.celer);

        if (!exist) {
            addMarketTob(prevPair.celer);
        } else {
            const quantity = exist.tickerState?.quantity;
            const hasQuantity = quantity && quantity > 0;
            const orderBookClosed = exist.tickerState?.orderBook !== 'opened';
            if (!hasQuantity && orderBookClosed) {
                addMarketTob(prevPair.celer);
            }
        }
    }
    // Upgrade and set current pair to active pair (full book)
    dispatch(setActivePair(currentPair));
    return addMarketFull(currPair);
}

export async function cancelMarket(credentials: User, marketItem: MarketItemSubscribed) {
    if (!marketItem.subscriptionId) {
        return Promise.reject(`MarketItem does not contain subscription id.`);
    }

    // Needed attributes in payload found by trial and error
    return marketDataServiceClient.unary.createPriceSubscriptionRequest(
        {
            assetType: AssetType.FX,
            clientRequestId: uuidv4(),
            exchangeCode: marketItem.exchangeCode,
            settlementType: marketItem.settlementType,
            marketDataRequestId: marketItem.subscriptionId,
            marketDataRequestType: MarketDataRequestType.DISABLE_SNAPSHOTS,
            securityCode: marketItem.securityCode,
            securityId: marketItem.securityCode
        },
        {
            metadata: Metadata({
                'authorization-token': credentials.authToken
            })
        }
    );
}

const generateRequest = (credentials, pair) => {
    const dataReqId = uuidv4();

    const { marketExchangeCode, marketSettlementType, marketProductType } = getMarketDetails(pair);

    const req = MarketDataSubscriptionRequest.fromPartial({
        assetType: AssetType.FX,
        clientRequestId: uuidv4(),
        currency: undefined,
        exchangeCode: marketExchangeCode,
        settlementType: marketSettlementType,
        marketDataBookType: MarketDataBookType.TOP_OF_THE_BOOK,
        marketDataMidrateLevelType: MarketDataMidrateLevelType.TOB,
        marketDataRequestId: dataReqId,
        marketDataRequestType: MarketDataRequestType.SNAPSHOT_PLUS_UPDATES,
        marketDataUpdateType: MarketDataUpdateType.INCREMENTAL, // alternatively 2 for full snapshot - full snapshot includes price book
        productType: marketProductType,
        requestedByUser: credentials.username,
        securityCode: pair,
        securityId: pair
    });

    return { dataReqId, req, pair };
};

const getMarketDetails = (pair) => {
    const state = store.getState();
    const instrumentConfigs = state.oho.instrumentConfigs;

    const marginInstrumentConfigs = instrumentConfigs['Margin'] || {};
    const deliverableInstrumentConfigs = instrumentConfigs['Deliverable'] || {};

    const marginConfig = marginInstrumentConfigs[pair];
    const deliverableConfig = deliverableInstrumentConfigs[pair];

    const config = marginConfig || deliverableConfig;

    const marketExchangeCode = exchangeCode;
    let marketSettlementType = 'SP';
    let marketProductType = ProductType.SPOT;

    if (['Index', 'Commodity'].includes(config?.type) && config?.product_type !== 'FX') {
        marketSettlementType = 'TOM';
        marketProductType = ProductType.CFD;
    }

    return { marketExchangeCode, marketSettlementType, marketProductType };
};
